import React, { useState, useCallback } from 'react';
import apiClient from '../../services/apiClient';
import { toast } from 'sonner';
import { 
  Search, 
  Upload, 
  Link, 
  FileText, 
  Sparkles, 
  Copy, 
  Check,
  Loader2,
  TrendingUp,
  Filter,
  MessageSquare,
  X,
  Image as ImageIcon
} from 'lucide-react';

export default function SeoOptimizerTab({ onAnalysisComplete }) {
  // Polling interval for job status updates (milliseconds)
  const POLL_INTERVAL = 15000;
  const MAX_RETRIES = 3;

  // Estados de inputs
  const [productName, setProductName] = useState('');
  const [mlUrl, setMlUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Estados del job
  const [analyzing, setAnalyzing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [jobId, setJobId] = useState(null);

  // Resultados
  const [result, setResult] = useState(null);
  const [copiedKeyword, setCopiedKeyword] = useState(null);
  const [copiedTitle, setCopiedTitle] = useState(null);

  // Manejar imagen
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen debe ser menor a 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  // Polling del job con reintentos
  const pollJobStatus = useCallback(async (id, retryCount = 0) => {    
    try {
      const { data } = await apiClient.get(`/api/seo/job-status/${id}`);
      
      if (data.status === 'processing' || data.status === 'pending') {
        setCurrentStep(data.step || 'Procesando...');
        setTimeout(() => pollJobStatus(id, 0), POLL_INTERVAL); // Reset retry count on success
      } else if (data.status === 'completed') {
        setResult(data.result);
        setAnalyzing(false);
        setCurrentStep('');
        toast.success('Análisis completado');
        
        // ══════════════════════════════════════════════════════════════════
        // COMPARTIR CONTEXTO CON PESTAÑA DE DESCRIPCIÓN
        // ══════════════════════════════════════════════════════════════════
        if (onAnalysisComplete && data.result) {
          const r = data.result;
          const keywords = (r.palabras_clave_ordenadas || []).map(k => 
            typeof k === 'object' ? k.palabra : k
          );
          const titles = r.sugerencias_titulos || [];
          
          onAnalysisComplete({
            productName: r.query_used || r.sustantivo_principal || productName,
            keywords: keywords,
            titles: titles,
            category: r.category_detected,
            attributes: r.atributos_detectados || {},
            timestamp: new Date()
          });
        }
      } else if (data.status === 'error') {
        toast.error(data.error || 'Error en el análisis');
        setAnalyzing(false);
        setCurrentStep('');
      }
    } catch (error) {
      console.error('Poll error:', error);
      
      // Reintentar si no hemos llegado al máximo
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying poll (${retryCount + 1}/${MAX_RETRIES})...`);
        setTimeout(() => pollJobStatus(id, retryCount + 1), 2000);
      } else {
        toast.error('Error consultando estado. Intenta de nuevo.');
        setAnalyzing(false);
        setCurrentStep('');
      }
    }
  }, [onAnalysisComplete, productName]);

  // Iniciar análisis
  const handleAnalyze = async () => {
    if (!productName && !mlUrl && !imageFile) {
      toast.error('Proporciona al menos un dato: nombre, URL o imagen');
      return;
    }

    setAnalyzing(true);
    setResult(null);
    setCurrentStep('Iniciando análisis...');

    try {
      const formData = new FormData();
      if (productName) formData.append('product_name', productName);
      if (mlUrl) formData.append('ml_url', mlUrl);
      if (imageFile) formData.append('image', imageFile);

      const { data } = await apiClient.post('/api/seo/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });

      setJobId(data.job_id);
      pollJobStatus(data.job_id);
    } catch (error) {
      console.error('Analyze error:', error);
      toast.error(error.response?.data?.detail || 'Error iniciando análisis');
      setAnalyzing(false);
    }
  };

  // Copiar al portapapeles
  const copyToClipboard = (text, type, index) => {
    navigator.clipboard.writeText(text);
    if (type === 'keyword') {
      setCopiedKeyword(index);
      setTimeout(() => setCopiedKeyword(null), 2000);
    } else {
      setCopiedTitle(index);
      setTimeout(() => setCopiedTitle(null), 2000);
    }
    toast.success('Copiado al portapapeles');
  };

  // Color según relevancia
  const getRelevanceColor = (relevancia) => {
    switch (relevancia) {
      case 'Alta': return 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/30';
      case 'Media': return 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/30';
      case 'Baja': return 'bg-slate-500/20 text-muted-foreground border-slate-500/30';
      default: return 'bg-slate-500/20 text-muted-foreground border-slate-500/30';
    }
  };

  return (
    <div className="space-y-8">
      {/* Descripción */}
      <div className="text-center space-y-3">
        <h2 className="text-3xl font-bold font-outfit text-foreground">Optimizador de Títulos SEO</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Extrae intención de búsqueda <span className="text-blue-600 dark:text-blue-400 font-semibold">real de Mercado Libre</span> para generar títulos optimizados que maximicen la visibilidad de tu producto.
        </p>
      </div>

      {/* Sección de Inputs */}
      <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Search className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Origen de Datos
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Puedes proporcionar uno, varios o todos los datos para mejor precisión
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Input: Nombre del producto */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              <FileText className="w-4 h-4 inline mr-2" />
              Nombre del Producto
            </label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Ej: Gorro satín para dormir"
              className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              data-testid="seo-product-name-input"
            />
          </div>

          {/* Input: URL de ML */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              <Link className="w-4 h-4 inline mr-2" />
              URL de Mercado Libre (Opcional)
            </label>
            <input
              type="url"
              value={mlUrl}
              onChange={(e) => setMlUrl(e.target.value)}
              placeholder="https://articulo.mercadolibre.com.co/..."
              className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              data-testid="seo-ml-url-input"
            />
          </div>

          {/* Input: Imagen */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              <ImageIcon className="w-4 h-4 inline mr-2" />
              Foto del Producto
            </label>
            {!imagePreview ? (
              <label className="flex flex-col items-center justify-center w-full h-[52px] bg-muted border border-border border-dashed rounded-xl cursor-pointer hover:bg-muted transition-colors">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">Subir imagen</span>
                </div>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageChange} 
                  className="hidden"
                  data-testid="seo-image-upload"
                />
              </label>
            ) : (
              <div className="relative h-[52px] bg-muted border border-border rounded-xl flex items-center px-3">
                <img src={imagePreview} alt="Preview" className="h-8 w-8 object-cover rounded" />
                <span className="ml-2 text-sm text-muted-foreground truncate flex-1">{imageFile?.name}</span>
                <button onClick={removeImage} className="p-1 hover:bg-slate-700 rounded">
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Botón de análisis */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={handleAnalyze}
            disabled={analyzing || (!productName && !mlUrl && !imageFile)}
            className="flex items-center gap-3 px-8 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold text-lg
              hover:from-blue-500 hover:to-cyan-500 shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-0.5
              disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            data-testid="seo-analyze-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {currentStep || 'Analizando...'}
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Extraer Palabras Clave y Generar Títulos
              </>
            )}
          </button>
        </div>
      </div>

      {/* Resultados */}
      {result && (
        <div className="space-y-6">
          {/* Info de fuentes */}
          <div className="flex flex-wrap gap-4 justify-center">
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
              <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm text-muted-foreground">
                Autosuggest: <span className="text-foreground font-medium">{result.fuentes_utilizadas?.autosuggest || 0}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
              <Filter className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-muted-foreground">
                Filtros: <span className="text-foreground font-medium">{result.fuentes_utilizadas?.filtros || 0}</span>
              </span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg border border-border">
              <TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm text-muted-foreground">
                Trends: <span className="text-foreground font-medium">{result.fuentes_utilizadas?.trends || 0}</span>
              </span>
            </div>
          </div>

          {/* SECCIÓN PRINCIPAL: Más Buscados en Categoría (Trends) */}
          {result.trends_categoria && result.trends_categoria.length > 0 && (
            <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-orange-500/30 p-6" data-testid="seo-trends-section">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                <div className="p-2 bg-orange-500/20 rounded-lg">
                  <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
                  Más Buscados en Categoría
                </span>
                <span className="text-base font-normal text-muted-foreground">
                  ({result.trends_categoria.length} tendencias)
                </span>
              </h3>
              
              {/* Leyenda de relevancia */}
              <div className="flex flex-wrap gap-4 mb-4 text-sm">
                <span className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 rounded-full border border-emerald-500/30">
                  <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                  <span className="text-emerald-600 dark:text-emerald-400">Alta (cat. general)</span>
                </span>
                <span className="flex items-center gap-2 px-3 py-1 bg-amber-500/10 rounded-full border border-amber-500/30">
                  <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                  <span className="text-amber-600 dark:text-amber-400">Media (cat. padre)</span>
                </span>
                <span className="flex items-center gap-2 px-3 py-1 bg-slate-500/10 rounded-full border border-slate-500/30">
                  <span className="w-3 h-3 rounded-full bg-slate-500"></span>
                  <span className="text-muted-foreground">Baja (cat. específica)</span>
                </span>
              </div>
              
              {/* Categorías usadas */}
              {result.categorias_trends && result.categorias_trends.length > 0 && (
                <p className="text-sm text-muted-foreground mb-4 flex items-center gap-2">
                  <span className="text-muted-foreground/70">Categorías analizadas:</span>
                  <span className="text-muted-foreground">
                    {result.categorias_trends.map(c => c.name).join(' → ')}
                  </span>
                </p>
              )}
              
              <div className="flex flex-wrap gap-2 max-h-[400px] overflow-y-auto pr-2">
                {result.trends_categoria.map((trend, idx) => {
                  const keyword = typeof trend === 'string' ? trend : trend.keyword;
                  const relevancia = typeof trend === 'string' ? 'Baja' : trend.relevancia;
                  const categoria = typeof trend === 'string' ? '' : trend.categoria;
                  
                  const getRelevanciaStyle = () => {
                    if (relevancia === 'Alta') return 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/30';
                    if (relevancia === 'Media') return 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/40 hover:bg-amber-500/30';
                    return 'bg-slate-700/30 text-muted-foreground border-border/30 hover:bg-slate-700/50';
                  };
                  
                  return (
                    <button
                      key={idx}
                      onClick={() => copyToClipboard(keyword, 'keyword', `trend-${idx}`)}
                      className={`group flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all hover:scale-105 ${getRelevanciaStyle()}`}
                      title={categoria ? `Categoría: ${categoria}` : ''}
                      data-testid={`seo-trend-chip-${idx}`}
                    >
                      <span className="text-sm">{keyword}</span>
                      <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Sección A: Palabras Clave Filtradas por IA */}
            <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Keywords Filtrados por IA
                <span className="text-sm font-normal text-muted-foreground">
                  ({result.palabras_clave_ordenadas?.length || 0})
                </span>
              </h3>
              <p className="text-sm text-muted-foreground mb-4">Solo los que aplican a tu producto</p>
              
              <div className="flex flex-wrap gap-2 max-h-[300px] overflow-y-auto pr-2">
                {result.palabras_clave_ordenadas?.map((kw, idx) => (
                  <button
                    key={idx}
                    onClick={() => copyToClipboard(kw.palabra, 'keyword', idx)}
                    className={`group flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all hover:scale-105 ${getRelevanceColor(kw.relevancia)}`}
                    data-testid={`seo-keyword-chip-${idx}`}
                  >
                    <span className="text-sm">{kw.palabra}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      kw.relevancia === 'Alta' ? 'bg-emerald-500/30' :
                      kw.relevancia === 'Media' ? 'bg-amber-500/30' : 'bg-slate-500/30'
                    }`}>
                      {kw.relevancia}
                    </span>
                    {copiedKeyword === idx ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Sección B: Títulos Sugeridos */}
            <div className="bg-card/60 backdrop-blur-xl rounded-2xl border border-border p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                Títulos Optimizados
              </h3>
              <p className="text-sm text-muted-foreground mb-4">3 títulos para publicación (60 car.) + 1 para catálogo (120 car.)</p>

              <div className="space-y-4">
                {result.sugerencias_titulos?.map((titulo, idx) => {
                  const isCatalog = idx === 3;
                  const maxChars = isCatalog ? 120 : 60;
                  const warnAt = isCatalog ? 100 : 50;
                  const accent = isCatalog ? 'amber' : 'purple';
                  return (
                    <div
                      key={idx}
                      className={`group relative bg-muted border rounded-xl p-4 transition-colors ${
                        isCatalog
                          ? 'border-amber-500/30 hover:border-amber-500/50'
                          : 'border-border hover:border-cyan-500/50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium ${
                              isCatalog
                                ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                                : 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400'
                            }`}>
                              {idx + 1}
                            </span>
                            {isCatalog && (
                              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                                Catálogo
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {titulo.length}/{maxChars} caracteres
                            </span>
                          </div>
                          <p className="text-foreground font-medium" data-testid={`seo-title-${idx}`}>
                            {titulo}
                          </p>
                        </div>
                        <button
                          onClick={() => copyToClipboard(titulo, 'title', idx)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                            isCatalog
                              ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-600 dark:text-amber-400'
                              : 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-600 dark:text-cyan-400'
                          }`}
                          data-testid={`seo-copy-title-${idx}`}
                        >
                          {copiedTitle === idx ? (
                            <>
                              <Check className="w-4 h-4" />
                              <span className="text-sm">Copiado</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              <span className="text-sm">Copiar</span>
                            </>
                          )}
                        </button>
                      </div>
                      {/* Barra de longitud */}
                      <div className="mt-3 h-1 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all ${
                            titulo.length > maxChars ? 'bg-red-500' :
                            titulo.length > warnAt
                              ? (isCatalog ? 'bg-amber-500' : 'bg-amber-500')
                              : (isCatalog ? 'bg-amber-400' : 'bg-emerald-500')
                          }`}
                          style={{ width: `${Math.min(titulo.length / maxChars * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Categoría detectada */}
          {result.category_detected && (
            <div className="text-center text-sm text-muted-foreground">
              Categoría detectada: <span className="text-muted-foreground">{result.category_detected}</span>
              {result.query_used && (
                <span className="ml-2">| Query: <span className="text-muted-foreground">"{result.query_used}"</span></span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Estado vacío */}
      {!result && !analyzing && (
        <div className="text-center py-12">
          <Search className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-muted-foreground mb-2">
            Extrae intención de búsqueda real
          </h3>
          <p className="text-muted-foreground/70 max-w-md mx-auto">
            Ingresa el nombre de tu producto, una URL de Mercado Libre, o sube una foto 
            para obtener palabras clave basadas en datos reales de búsqueda.
          </p>
        </div>
      )}
    </div>
  );
}
